import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
// Mock data imports removed - using backend data only
import { bookingsAPI, leadsAPI } from '../services/api';
import { formatCurrency } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { 
  Calendar, 
  TrendingUp, 
  CheckCircle,
  CalendarCheck,
  BarChart3,
  PieChart,
  ArrowLeft,
  Users,
  RefreshCw,
  Download,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import useFinancialVisibility from '../hooks/useFinancialVisibility';
import usePermissions from '../hooks/usePermissions';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BookingAnalytics = () => {
  const navigate = useNavigate();
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { toast } = useToast();
  const hideFinancials = useFinancialVisibility();
  const { canExportBookingAnalytics } = usePermissions();
  
  const [yearType, setYearType] = useState('calendar'); // 'calendar', 'financial', or 'custom'
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [customStartDate, setCustomStartDate] = useState(new Date(currentYear, 0, 1).toISOString().split('T')[0]); // Jan 1 of current year
  const [customEndDate, setCustomEndDate] = useState(new Date().toISOString().split('T')[0]); // Today
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const currentMonth = currentDate.getMonth();

  // Function to load data - defined for refresh button
  const loadData = async () => {
    setIsLoading(true);
    try {
      const bookingsData = await bookingsAPI.getAll();
      const mappedBookings = bookingsData.map(booking => ({
        id: booking.id,
        customerName: booking.customerName,
        venueName: booking.venueName,
        eventDate: booking.eventDate,
        eventType: booking.eventType,
        status: booking.status?.toLowerCase() || 'confirmed',
        amount: booking.amount,
        guests: booking.guests,
        advancePaid: booking.advancePaid || 0
      }));
      setBookings(mappedBookings);
    } catch (error) {
      console.error('Failed to load bookings:', error);
      setBookings([]);
    }

    try {
      const leadsData = await leadsAPI.getAll();
      setLeads(leadsData);
    } catch (error) {
      console.error('Failed to load leads:', error);
      setLeads([]);
    }
    
    setIsLoading(false);
  };

  // Load data on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Generate year options (current year ± 3 years)
  const yearOptions = [];
  for (let i = -3; i <= 3; i++) {
    yearOptions.push(currentYear + i);
  }

  // Calculate year range based on selection
  const getYearRange = () => {
    if (yearType === 'custom') {
      // Custom date range
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      return {
        startMonth: startDate.getMonth(),
        startYear: startDate.getFullYear(),
        endMonth: endDate.getMonth(),
        endYear: endDate.getFullYear(),
        startDate: startDate,
        endDate: endDate,
        isCustom: true
      };
    } else if (yearType === 'financial') {
      // Financial Year: April to March (e.g., FY 2024-25 = April 2024 to March 2025)
      return {
        startMonth: 3, // April
        startYear: selectedYear,
        endMonth: 2, // March
        endYear: selectedYear + 1,
        isCustom: false
      };
    } else {
      // Calendar Year: January to December
      return {
        startMonth: 0, // January
        startYear: selectedYear,
        endMonth: 11, // December
        endYear: selectedYear,
        isCustom: false
      };
    }
  };

  const yearRange = getYearRange();
  
  // Get display year text
  const getYearDisplayText = () => {
    if (yearType === 'custom') {
      const formatShortDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      };
      return `${formatShortDate(customStartDate)} to ${formatShortDate(customEndDate)}`;
    } else if (yearType === 'financial') {
      return `FY ${selectedYear}-${(selectedYear + 1).toString().slice(-2)}`;
    } else {
      return selectedYear.toString();
    }
  };

  // Get report data
  const getReportData = () => {
    const headers = hideFinancials 
      ? ['Customer Name', 'Venue', 'Event Date', 'Event Type', 'Status']
      : ['Customer Name', 'Venue', 'Event Date', 'Event Type', 'Status', 'Amount (₹)'];
    
    const data = thisYearBookings.map(booking => {
      const row = {
        'Customer Name': booking.customerName || '',
        'Venue': booking.venueName || '',
        'Event Date': new Date(booking.eventDate).toLocaleDateString(),
        'Event Type': booking.eventType || '',
        'Status': booking.status || ''
      };
      if (!hideFinancials) {
        row['Amount (₹)'] = booking.amount || 0;
      }
      return row;
    });

    return { headers, data };
  };

  // Export as CSV
  const exportAsCSV = () => {
    try {
      const { headers, data } = getReportData();
      const csvContent = [
        `Booking Analytics Report - ${getYearDisplayText()}`,
        `Generated: ${new Date().toLocaleString()}`,
        '',
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `booking_analytics_${getYearDisplayText().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({ title: 'Export Successful', description: `CSV exported with ${data.length} bookings` });
    } catch (error) {
      console.error('CSV export failed:', error);
      toast({ title: 'Export Failed', description: 'Failed to export CSV', variant: 'destructive' });
    }
  };

  // Export as Excel
  const exportAsExcel = () => {
    try {
      const { headers, data } = getReportData();
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      
      // Add title rows
      const titleData = [
        [`Booking Analytics Report - ${getYearDisplayText()}`],
        [`Generated: ${new Date().toLocaleString()}`],
        [],
        headers
      ];
      
      // Add data rows
      data.forEach(row => {
        titleData.push(headers.map(h => row[h]));
      });

      const ws = XLSX.utils.aoa_to_sheet(titleData);
      
      // Set column widths
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      
      XLSX.utils.book_append_sheet(wb, ws, 'Booking Analytics');
      XLSX.writeFile(wb, `booking_analytics_${getYearDisplayText().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast({ title: 'Export Successful', description: `Excel exported with ${data.length} bookings` });
    } catch (error) {
      console.error('Excel export failed:', error);
      toast({ title: 'Export Failed', description: 'Failed to export Excel', variant: 'destructive' });
    }
  };

  // Export as PDF
  const exportAsPDF = () => {
    try {
      const { headers, data } = getReportData();
      
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.setTextColor(0, 128, 128);
      doc.text('Booking Analytics Report', 14, 20);
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(getYearDisplayText(), 14, 28);
      doc.text('Generated: ' + new Date().toLocaleString('en-IN'), 14, 35);
      
      // Add summary stats
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text('Total Bookings: ' + thisYearBookings.length, 14, 45);
      doc.text('Confirmed: ' + thisYearBookings.filter(b => b.status === 'confirmed').length, 14, 52);
      doc.text('Blocked: ' + thisYearBookings.filter(b => b.status === 'blocked').length, 80, 52);
      
      // Fix headers and data - replace rupee symbol
      const fixedHeaders = headers.map(h => h.replace(/₹/g, 'Rs.'));
      const fixedData = data.map(row => {
        const fixedRow = {};
        headers.forEach(h => {
          let value = String(row[h] || '');
          value = value.replace(/₹/g, 'Rs.');
          fixedRow[h.replace(/₹/g, 'Rs.')] = value;
        });
        return fixedRow;
      });
      
      // Add table using autoTable
      autoTable(doc, {
        head: [fixedHeaders],
        body: fixedData.map(row => fixedHeaders.map(h => String(row[h] || ''))),
        startY: 60,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [0, 128, 128], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 248, 255] }
      });

      doc.save('booking_analytics_' + getYearDisplayText().replace(/\s+/g, '_') + '_' + new Date().toISOString().split('T')[0] + '.pdf');

      toast({ title: 'Export Successful', description: 'PDF exported with ' + data.length + ' bookings' });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({ title: 'Export Failed', description: 'Failed to export PDF: ' + error.message, variant: 'destructive' });
    }
  };

  // Calculate this year's bookings (or custom date range)
  const thisYearBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    
    // For custom date range, use direct date comparison
    if (yearRange.isCustom) {
      const startDate = new Date(customStartDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      return bookingDate >= startDate && bookingDate <= endDate;
    }
    
    // For calendar/financial year, use month-based comparison
    const bookingYear = bookingDate.getFullYear();
    const bookingMonth = bookingDate.getMonth();
    
    if (yearRange.startYear === yearRange.endYear) {
      return bookingYear === yearRange.startYear &&
             bookingMonth >= yearRange.startMonth &&
             bookingMonth <= yearRange.endMonth;
    } else {
      return (bookingYear === yearRange.startYear && bookingMonth >= yearRange.startMonth) ||
             (bookingYear === yearRange.endYear && bookingMonth <= yearRange.endMonth);
    }
  });

  // Calculate this month's bookings
  const thisMonthBookings = bookings.filter(b => {
    const bookingDate = new Date(b.eventDate);
    return bookingDate.getMonth() === currentMonth && bookingDate.getFullYear() === currentYear;
  });

  // Status breakdown (only Confirmed and Blocked)
  const statusBreakdown = {
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    blocked: bookings.filter(b => b.status === 'blocked').length
  };

  // Event type breakdown
  const eventTypes = {};
  bookings.forEach(b => {
    eventTypes[b.eventType] = (eventTypes[b.eventType] || 0) + 1;
  });

  // Monthly trends for interactive graph (last 6 months)
  const monthlyTrends = [];
  for (let i = 5; i >= 0; i--) {
    const targetDate = new Date(currentYear, currentMonth - i, 1);
    const monthBookings = bookings.filter(b => {
      const bookingDate = new Date(b.eventDate);
      return bookingDate.getMonth() === targetDate.getMonth() && 
             bookingDate.getFullYear() === targetDate.getFullYear();
    });
    
    const monthLeads = leads.filter(l => {
      // Use createdAt for leads (when the lead was created)
      const leadDate = new Date(l.createdAt);
      return leadDate.getMonth() === targetDate.getMonth() && 
             leadDate.getFullYear() === targetDate.getFullYear();
    });
    
    monthlyTrends.push({
      month: targetDate.toLocaleDateString('en-IN', { month: 'short' }),
      bookings: monthBookings.length,
      leads: monthLeads.length,
      revenue: monthBookings.reduce((sum, b) => sum + b.amount, 0)
    });
  }

  // Average booking value
  const totalRevenue = bookings.reduce((sum, b) => sum + b.amount, 0);
  const avgBookingValue = bookings.length > 0 ? totalRevenue / bookings.length : 0;

  // Calculate Highest Booking Month for selected year
  const monthlyBookingCounts = {};
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  
  thisYearBookings.forEach(booking => {
    const bookingDate = new Date(booking.eventDate);
    const monthKey = `${bookingDate.getFullYear()}-${bookingDate.getMonth()}`;
    const monthName = monthNames[bookingDate.getMonth()];
    
    if (!monthlyBookingCounts[monthKey]) {
      monthlyBookingCounts[monthKey] = { 
        name: monthName, 
        shortName: monthName.slice(0, 3),
        count: 0, 
        revenue: 0 
      };
    }
    monthlyBookingCounts[monthKey].count++;
    monthlyBookingCounts[monthKey].revenue += booking.amount || 0;
  });

  // Find highest booking month
  let highestBookingMonth = { name: 'N/A', shortName: 'N/A', count: 0, revenue: 0 };
  Object.values(monthlyBookingCounts).forEach(month => {
    if (month.count > highestBookingMonth.count) {
      highestBookingMonth = month;
    }
  });

  const summaryStats = hideFinancials ? [
    {
      title: `Total Bookings (${getYearDisplayText()})`,
      value: thisYearBookings.length,
      icon: CalendarCheck,
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
      textColor: 'text-teal-600'
    },
    {
      title: 'This Month',
      value: thisMonthBookings.length,
      icon: Calendar,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-600'
    },
    {
      title: 'Highest Booking Month',
      value: highestBookingMonth.count > 0 ? `${highestBookingMonth.shortName} (${highestBookingMonth.count})` : 'N/A',
      icon: Trophy,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      textColor: 'text-amber-600',
      subtitle: highestBookingMonth.count > 0 ? `${highestBookingMonth.name}` : 'No bookings yet'
    }
  ] : [
    {
      title: `Total Bookings (${getYearDisplayText()})`,
      value: thisYearBookings.length,
      icon: CalendarCheck,
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-50 dark:bg-teal-900/20',
      textColor: 'text-teal-600'
    },
    {
      title: 'This Month',
      value: thisMonthBookings.length,
      icon: Calendar,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-600'
    },
    {
      title: 'Highest Booking Month',
      value: highestBookingMonth.count > 0 ? `${highestBookingMonth.shortName} (${highestBookingMonth.count})` : 'N/A',
      icon: Trophy,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      textColor: 'text-amber-600',
      subtitle: highestBookingMonth.count > 0 ? `Revenue: ${formatCurrency(highestBookingMonth.revenue)}` : 'No bookings yet'
    },
    {
      title: 'Avg Booking Value',
      value: formatCurrency(avgBookingValue),
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-600'
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(totalRevenue),
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      textColor: 'text-purple-600'
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:text-foreground -ml-2 h-8 sm:h-10 text-sm"
      >
        <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-foreground">Booking Analytics</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-1">Comprehensive insights into your booking performance</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button 
            onClick={loadData}
            variant="outline"
            className="flex items-center gap-1.5 sm:gap-2 h-8 sm:h-10 text-xs sm:text-sm"
            disabled={isLoading}
          >
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Select value={yearType} onValueChange={setYearType}>
            <SelectTrigger className="w-[130px] sm:w-[180px] h-8 sm:h-10 text-xs sm:text-sm">
              <SelectValue placeholder="Select Year Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="calendar">Calendar Year</SelectItem>
              <SelectItem value="financial">Financial Year</SelectItem>
              <SelectItem value="custom">Custom Date</SelectItem>
            </SelectContent>
          </Select>
          {yearType !== 'custom' ? (
            <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
              <SelectTrigger className="w-[100px] sm:w-[140px] h-8 sm:h-10 text-xs sm:text-sm">
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {yearType === 'financial' ? `FY ${year}-${(year + 1).toString().slice(-2)}` : year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-[110px] sm:w-[140px] h-8 sm:h-10 text-xs sm:text-sm"
              />
              <span className="text-xs text-gray-500 dark:text-muted-foreground">to</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-[110px] sm:w-[140px] h-8 sm:h-10 text-xs sm:text-sm"
              />
            </div>
          )}
          {canExportBookingAnalytics() && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 h-8 sm:h-10 text-xs sm:text-sm px-2 sm:px-4">
                  <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Export</span>
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportAsCSV}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAsExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export as Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAsPDF}>
                  <FileText className="w-4 h-4 mr-2 text-red-500" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {summaryStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600 dark:text-muted-foreground mb-0.5 sm:mb-1 truncate">{stat.title}</p>
                    <p className="text-lg sm:text-xl lg:text-3xl font-bold text-gray-900 dark:text-foreground truncate">{stat.value}</p>
                    {stat.subtitle && (
                      <p className="text-[9px] sm:text-xs text-gray-500 dark:text-muted-foreground mt-0.5 sm:mt-1 truncate">{stat.subtitle}</p>
                    )}
                  </div>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 ${stat.bgColor} rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 ${stat.textColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Status Breakdown & Event Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Status Breakdown */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 p-3 sm:p-4 lg:p-6">
            <CardTitle className="flex items-center space-x-2 text-sm sm:text-base lg:text-lg dark:text-foreground">
              <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
              <span>Booking Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <div className="space-y-2 sm:space-y-4">
              <div className="flex items-center justify-between p-2.5 sm:p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                  <span className="font-medium text-gray-900 dark:text-foreground text-xs sm:text-base">Confirmed</span>
                </div>
                <span className="text-lg sm:text-2xl font-bold text-green-600">{statusBreakdown.confirmed}</span>
              </div>
              <div className="flex items-center justify-between p-2.5 sm:p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                  <span className="font-medium text-gray-900 dark:text-foreground text-xs sm:text-base">Blocked</span>
                </div>
                <span className="text-lg sm:text-2xl font-bold text-red-600">{statusBreakdown.blocked}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Event Type Distribution */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 p-3 sm:p-4 lg:p-6">
            <CardTitle className="flex items-center space-x-2 text-sm sm:text-base lg:text-lg dark:text-foreground">
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              <span>Event Types</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <div className="space-y-2 sm:space-y-3">
              {Object.entries(eventTypes)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count], index) => (
                  <div key={index} className="flex items-center justify-between gap-2">
                    <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground truncate flex-shrink-0 w-20 sm:w-auto">{type}</span>
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-1 justify-end">
                      <div className="w-16 sm:w-32 h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-pink-600 rounded-full"
                          style={{ width: `${(count / bookings.length) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-gray-900 dark:text-foreground w-6 sm:w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Interactive Graph - Leads vs Bookings */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-3 sm:p-4 lg:p-6">
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base lg:text-lg dark:text-foreground">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            <span className="truncate">Leads vs Bookings (6 Months)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 lg:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Legend */}
            <div className="flex items-center justify-center space-x-4 sm:space-x-6">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded"></div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground">Bookings</span>
              </div>
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-orange-500 to-amber-600 rounded"></div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground">Leads</span>
              </div>
            </div>
            
            {/* Graph */}
            <div className="space-y-3 sm:space-y-4">
              {monthlyTrends.map((trend, index) => {
                const maxValue = Math.max(...monthlyTrends.map(t => Math.max(t.bookings, t.leads)));
                const bookingPercent = (trend.bookings / maxValue) * 100;
                const leadPercent = (trend.leads / maxValue) * 100;
                
                return (
                  <div key={index} className="space-y-1.5 sm:space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] sm:text-sm font-medium text-gray-700 dark:text-foreground w-10 sm:w-16">{trend.month}</span>
                      <div className="flex-1 mx-2 sm:mx-4 space-y-1 sm:space-y-1.5">
                        {/* Bookings Bar */}
                        <div className="relative">
                          <div className="w-full h-4 sm:h-6 bg-blue-100 dark:bg-blue-900/30 rounded sm:rounded-lg overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded sm:rounded-lg flex items-center justify-end pr-1.5 sm:pr-3 transition-all duration-500 hover:opacity-90"
                              style={{ width: `${Math.max(bookingPercent, 15)}%` }}
                            >
                              <span className="text-[8px] sm:text-xs font-bold text-white">{trend.bookings}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Leads Bar */}
                        <div className="relative">
                          <div className="w-full h-4 sm:h-6 bg-orange-100 dark:bg-orange-900/30 rounded sm:rounded-lg overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-orange-500 to-amber-600 rounded sm:rounded-lg flex items-center justify-end pr-1.5 sm:pr-3 transition-all duration-500 hover:opacity-90"
                              style={{ width: `${Math.max(leadPercent, 15)}%` }}
                            >
                              <span className="text-[8px] sm:text-xs font-bold text-white">{trend.leads}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {!hideFinancials && (
                        <span className="text-[10px] sm:text-sm font-bold text-gray-900 dark:text-foreground w-16 sm:w-24 text-right truncate">{formatCurrency(trend.revenue)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingAnalytics;
